import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import { useSnackbar } from "components/GlobalSnackbar";
import CloseIcon from "@mui/icons-material/Close";
import { getCurrentUser, getCurrentUserDepartment } from "utils/userUtils";
import { buttonStyles } from "utils/buttonStyles";
import { dialogHeaderStyles } from "utils/dialogStyles";

function TambahDeposit({ open, onClose, onSave, noAkaun }) {
  const [formState, setFormState] = useState({
    no_akaun: noAkaun || "",
    perkara: "",
    jumlah: "",
  });

  const [errors, setErrors] = useState({});
  const { showSuccess, showError } = useSnackbar();
  const [saving, setSaving] = useState(false); // prevent double submit

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState({
      ...formState,
      [name]: value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formState.perkara.trim()) {
      newErrors.perkara = "Perkara diperlukan";
    }

    if (!formState.jumlah.trim()) {
      newErrors.jumlah = "Jumlah diperlukan";
    } else if (isNaN(parseFloat(formState.jumlah)) || parseFloat(formState.jumlah) <= 0) {
      newErrors.jumlah = "Jumlah mesti nombor positif";
    }

  // no_resit removed; backend will auto-generate

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (saving) return; // guard
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      // Build payload according to requirements
      const user = getCurrentUser() || {};
      const department = getCurrentUserDepartment();

      // Today date without time in local timezone (YYYY-MM-DD)
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const tarikhResit = `${yyyy}-${mm}-${dd}`;

      const todayIso = new Date().toISOString();
      const depositData = {
        // id will be auto-assigned by backend (max+1)
        no_akaun: noAkaun,
        tarikh_resit: tarikhResit,
        tarikh_simpan: todayIso,
        perkara: (formState.perkara || "").toLocaleUpperCase("ms-MY"),
        jumlah: parseFloat(formState.jumlah),
        baki: 0.0,
        id_pengguna: (user.id_pengguna || user.username || "").toLowerCase(),
        mark_delete: 0,
        bahagian_pemantau: department,
        // no_resit will be auto-generated by backend
      };

      await onSave(depositData);

      // Show success snackbar then close and reset
      showSuccess("Deposit berjaya disimpan.");
      setTimeout(() => {
        handleClose();
      }, 1200);
    } catch (error) {
      console.error("Error saving deposit:", error);
      showError("Gagal menyimpan deposit. Sila cuba lagi.");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    // Reset form state
    setFormState({
      no_akaun: noAkaun || "",
      perkara: "",
      jumlah: "",
    });
    setErrors({});
    onClose();
  };

  // Update no_akaun when prop changes
  useEffect(() => {
    if (noAkaun) {
      setFormState((prev) => ({ ...prev, no_akaun: noAkaun }));
    }
  }, [noAkaun]);

  return (
    <>
      <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        },
      }}
      >
      <DialogTitle
        sx={{
          ...dialogHeaderStyles.special,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ color: "#fff" }}>
          Tambah Deposit
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: "white" }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Maklumat Deposit
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="No Akaun"
              name="no_akaun"
              value={formState.no_akaun}
              disabled
              variant="outlined"
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#5b5e70ff",
                  backgroundColor: "#ffffffff",
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Perkara *"
              name="perkara"
              value={formState.perkara}
              onChange={handleChange}
              error={!!errors.perkara}
              helperText={errors.perkara}
              variant="outlined"
              placeholder="Contoh: WANG AMANAH SEWAAN"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Jumlah RM *"
              name="jumlah"
              value={formState.jumlah}
              onChange={handleChange}
              error={!!errors.jumlah}
              helperText={errors.jumlah}
              variant="outlined"
              type="number"
              inputProps={{
                step: "0.01",
                min: "0",
              }}
              placeholder="0.00"
            />
          </Grid>

          {/* No Resit field removed; generated by backend */}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={saving}
          sx={{
            ...buttonStyles.save,
            m: 0,
            px: 2,
            minWidth: 0
          }}
        >
          {saving ? 'MENYIMPAN...' : 'SIMPAN'}
        </Button>
        <Button
          onClick={handleClose}
          variant="contained"
          size="small"
          sx={{
            ...buttonStyles.cancel,
            m: 0,
            px: 2,
            minWidth: 0
          }}
        >
          BATAL
        </Button>
      </DialogActions>
      </Dialog>
    </>
  );
}

TambahDeposit.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  noAkaun: PropTypes.string,
};

TambahDeposit.defaultProps = {
  noAkaun: "",
};

export default TambahDeposit;
